import { format, parseISO, type Locale } from 'date-fns';

import { PointExpiryPeriodMutationPayload } from '../../queries/point-expiry';
import { PointExpiryPeriod } from '../../schemas/point-expiry';
// Type-only: schema.ts imports toDateInputValue from this file, so a value import here would form a
// runtime cycle. `import type` is erased at build time, leaving a one-way schema -> helpers edge.
import type { PointExpiryFormData } from './schema';

export const POINT_EXPIRY_INDEX_PATH = '/admin/recognition/point-expiry';

export const pointExpiryEditPath = (id: string): string => `${POINT_EXPIRY_INDEX_PATH}/${id}/edit`;

// Collapses an ISO datetime (or a `YYYY-MM-DD` form value) to a day-only string;
// blank/null becomes ''. The single definition for the whole module — the form, the schema and the
// record mapping all normalize dates through here so a future date-handling fix lands once.
export const toDateInputValue = (value: string | null | undefined): string => (value ? value.split('T')[0] : '');

export const toPayload = (data: PointExpiryFormData): PointExpiryPeriodMutationPayload => ({
	startDate: data.startDate,
	endDate: data.endDate,
	warningDate: data.warningDate,
});

// Renders a period date for display (e.g. "Dec 31, 2026"). The record dates are
// midnight-UTC ISO strings; we parse only the day component so a negative local
// offset can't shift the shown date back a day. Blank/null yields ''.
//
// Pass the caller's `getDateFnsLocale(i18n.language)` so the month name is translated to match the
// surrounding copy; omitting it falls back to date-fns' English default.
export const formatPeriodDate = (value: string | null | undefined, locale?: Locale): string => {
	const day = toDateInputValue(value);
	return day ? format(parseISO(day), 'MMM d, yyyy', { locale }) : '';
};

export const toFormValues = (period: PointExpiryPeriod): PointExpiryFormData => ({
	startDate: toDateInputValue(period.startAt),
	endDate: toDateInputValue(period.endAt),
	warningDate: toDateInputValue(period.warningAt),
});
